import { useEffect, useState } from 'react';
import { getToken } from "../../../src/utils/tokenUtils";
import axios from 'axios';
import Drawer from '../../components/UI/Drawer';
import { Search, Eye, FileText } from 'lucide-react';
import Notification from "../../components/UI/Notification";
import DeleteButton from '../../components/UI/AlertDialog';
import { useLocation } from 'react-router-dom';

const FormResponsePage = ({ formId = null }) => {
  const [formResponses, setFormResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = getToken();
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQueries, setSearchQueries] = useState('');
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const location = useLocation();
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [mainTab, setMainTab] = useState('internal'); 

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const endpoint = formId
          ? `https://hrmsnew.prepstripe.com/api/form-responses/${formId}`
          : 'https://hrmsnew.prepstripe.com/api/all-form-responses';

        const res = await axios.get(endpoint, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        const data = res.data.data || {};
        const transformed = Object.entries(data).map(([slug, entry]) => ({
          ...entry,
          form_slug: slug,
        }));

        setFormResponses(transformed); 
        if (transformed.length > 0 && !selectedFormId) {
          setSelectedFormId(transformed[0].form_id || transformed[0].id);
        }
      } catch (err) {
        setError('Failed to load responses. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [token, formId]);

  // Auto-open drawer if form_id and submission_id are in query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const form_id = params.get('form_id');
    const submission_id = params.get('submission_id');
    if (form_id && submission_id && formResponses.length > 0) {
      const form = formResponses.find(f => String(f.id) === String(form_id) || String(f.form_id) === String(form_id));
      if (form && form.submissions) {
        const submission = form.submissions.find(s => String(s.id) === String(submission_id));
        if (submission) {
          setSelectedSubmission({ formName: form.form_name, form_id: form.id, ...submission });
          setEditValues(submission);
          setIsEditing(false);
          setIsDrawerOpen(true);
        }
      }
    }
  }, [location.search, formResponses]);

  // update response
  const handleSubmitUpdate = async (submissionId, form_id) => {
    // Extract form_id and formName but keep them for later use
    const { form_id: _, ...payload } = editValues;
    const formName = selectedSubmission?.formName || '';

    try {
      const res = await axios.post(
        `https://hrmsnew.prepstripe.com/api/${form_id}/form-submission/${submissionId}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedSubmission = {
        ...res.data.data,  
        form_id,           
        formName,          
      };

      setFormResponses((prev) =>
        prev.map((form) => ({
          ...form,
          submissions: form.submissions.map((s) =>
            s.id === submissionId ? updatedSubmission : s
          ),
        }))
      );

      setSelectedSubmission(updatedSubmission);
      setNotification({ message: "Form response updated", type: "success" });
      setIsEditing(false);
    } catch (err) {
      console.error("Update error:", err);
      setNotification({ message: "Failed to update submission.", type: "error" });
    }
  };

  // delete response
  const handleDelete = async (submissionId, form_id) => {

    try {
      // console.log("submission_id: ", submissionId, "form_id: ", form_id)
      await axios.delete(`https://hrmsnew.prepstripe.com/api/${form_id}/form-submission/${submissionId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      // Remove the deleted submission from state
      setFormResponses((prev) =>
        prev.map((form) => ({
          ...form,
          submissions: form.submissions.filter((s) => s.id !== submissionId),
        }))
      );

      setNotification({message: "Form response deleted", type: "success"})

    } catch (err) {
      console.error("Delete failed:", err);
      setNotification({message: "Failed to delete submission. Please try again.", type: "error"})
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
  };

  // Apply filters to submissions
  const getFilteredSubmissions = (submissions, displayKeys) => {
    if (!submissions) return [];
    
    return submissions.filter(submission => {
      // First apply search filter
      const searchContent = Object.values(submission).join(' ').toLowerCase();
      const searchMatch = searchContent.includes(searchQueries[selectedFormId]?.toLowerCase() || '');
      
      // Then apply column filters
      const filterMatch = displayKeys.every(key => {
        const filterValue = filters[key];
        if (!filterValue) return true;
        
        const cellValue = submission[key]?.toString().toLowerCase() || '';
        return cellValue.includes(filterValue.toLowerCase());
      });
      
      return searchMatch && filterMatch;
    });
  };

  // Filter forms by main tab
  const filteredFormResponses = formResponses.filter(form => {
    if (mainTab === 'internal') return form.form_type !== 'external';
    if (mainTab === 'external') return form.form_type === 'external';
    return true;
  });

  // Ensure first tab is selected when switching mainTab
  useEffect(() => {
    if (filteredFormResponses.length > 0) {
      setSelectedFormId(filteredFormResponses[0].form_id || filteredFormResponses[0].id);
    }
  }, [mainTab, filteredFormResponses.length]);

  // Format the date string to a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  const EmptyState = ({ message }) => (
    <div className="text-center py-12">
      <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
      <p className="text-lg text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="container mx-auto px-2 py-2">

        {/* notification */}
        {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
        )}

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Form Responses
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage all form submissions
          </p>
        </div>
        
        {/* Main Tabs: Internal/External */}
        <div className="flex gap-2 mb-4">
          <button
            className={`px-6 py-2 rounded-t-lg font-semibold transition-colors duration-200 focus:outline-none
              ${mainTab === 'internal' ? 'bg-white dark:bg-gray-900 border-b-2 border-slate-600 text-slate-900 dark:text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
            onClick={() => setMainTab('internal')}
          >
            Internal Forms
          </button>
          <button
            className={`px-6 py-2 rounded-t-lg font-semibold transition-colors duration-200 focus:outline-none
              ${mainTab === 'external' ? 'bg-white dark:bg-gray-900 border-b-2 border-slate-600 text-slate-900 dark:text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
            onClick={() => setMainTab('external')}
          >
            External Forms
          </button>
        </div>
        {/* Tabs for form names */}
        <div className="overflow-x-auto whitespace-nowrap mb-6 border-b border-gray-200 dark:border-gray-700 scrollbar-hide">
          <div className="flex gap-2 min-w-max px-1">
            {filteredFormResponses.map((form) => (
              <button
                key={form.form_id || form.id}
                onClick={() => setSelectedFormId(form.form_id || form.id)}
                className={`inline-block flex-shrink-0 px-5 py-2 rounded-t-lg font-semibold transition-colors duration-200 focus:outline-none
                  ${selectedFormId === (form.form_id || form.id)
                    ? 'bg-white dark:bg-gray-900 border-b-2 border-slate-600 text-slate-900 dark:text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}>
                {form.form_name}
              </button>
            ))}
          </div>
        </div>

        {/* Content Section */}
        <div className="space-y-8">
          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-red-200 dark:border-red-800 p-8">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
              </div>
            </div>
          ) : filteredFormResponses.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
              <EmptyState message="No responses available." />
            </div>
          ) : (
            (() => {
              const selectedForm = filteredFormResponses.find(f => (f.form_id || f.id) === selectedFormId);
              if (!selectedForm) return null;
              const formSearch = searchQueries[selectedForm.form_id] || '';

              // Determine the 4 columns to show
              let displayKeys = [];
              if (selectedForm.submissions && selectedForm.submissions.length > 0) {
                const firstSubmission = selectedForm.submissions[0];
                const allKeys = Object.keys(firstSubmission).filter(
                  (key) =>
                    !['id', 'submission_id', 'created_at', 'updated_at', 'created_by', 'form_id'].includes(key)
                );
                // Priority fields (case-insensitive, allow camel or snake)
                const priorityFields = [
                  'candidate_name', 'email', 'contact_number', 'experience'
                ];
                // Find actual keys in the data for each priority field
                const foundPriority = [];
                const lowerAllKeys = allKeys.map(k => k.toLowerCase());
                priorityFields.forEach(field => {
                  const idx = lowerAllKeys.findIndex(k => k.replace(/_/g, '') === field.replace(/_/g, ''));
                  if (idx !== -1) foundPriority.push(allKeys[idx]);
                });
                // Fill up to 4 columns
                displayKeys = [...foundPriority];
                allKeys.forEach(k => {
                  if (displayKeys.length < 4 && !displayKeys.includes(k)) displayKeys.push(k);
                });
                displayKeys = displayKeys.slice(0, 4);
              }

              return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {/* Form Header */}
                  <div className="bg-gray-100 px-8 py-6">
                    <h2 className="text-2xl font-bold text-black mb-2">{selectedForm.form_name}</h2>
                    <p className="text-gray-600">
                      {selectedForm.submissions?.length || 0} submission{selectedForm.submissions?.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {/* Search and Filter Section */}
                  <div className="p-6 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col lg:flex-row gap-4">
                      {/* Search */}
                      <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder={`Search in ${selectedForm.form_name}`}
                          value={formSearch}
                          onChange={(e) =>
                            setSearchQueries((prev) => ({
                              ...prev,
                              [selectedForm.form_id]: e.target.value,
                            }))
                          }
                          className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200"
                        />
                      </div>
                      
                      {/* Filter Toggle */}
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                        </svg>
                        Filters
                        {Object.keys(filters).length > 0 && (
                          <span className="bg-slate-600 text-white text-xs rounded-full px-2 py-1">
                            {Object.keys(filters).length}
                          </span>
                        )}
                      </button>
                    </div>
                    
                    {/* Filter Controls */}
                    {showFilters && (
                      <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filter by columns</h3>
                          <button
                            onClick={clearFilters}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Clear all
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {displayKeys.map((key) => (
                            <div key={key} className="space-y-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                {key.replace(/_/g, ' ')}
                              </label>
                              <input
                                type="text"
                                placeholder={`Filter ${key.replace(/_/g, ' ')}`}
                                value={filters[key] || ''}
                                onChange={(e) =>
                                  setFilters(prev => ({
                                    ...prev,
                                    [key]: e.target.value
                                  }))
                                }
                                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedForm.submissions && selectedForm.submissions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100 dark:bg-gray-900">
                          <tr>
                            {displayKeys.map((key) => (
                              <th
                                key={key}
                                className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                              >
                                {key.replace(/_/g, ' ')}
                              </th>
                            ))}
                            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {getFilteredSubmissions(selectedForm.submissions, displayKeys)
                            .map((submission, index) => {
                              return (
                                <tr key={index} className="hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-200">
                                  {displayKeys.map((key) => (
                                    <td
                                      key={key}
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300"
                                    >
                                      {submission[key] || '-'}
                                    </td>
                                  ))}
                                  <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex justify-center items-center space-x-3">
                                      <button
                                        onClick={() => {
                                          setSelectedSubmission({
                                            formName: selectedForm.form_name,
                                            form_id: selectedForm.id,
                                            ...submission,
                                          });
                                          setEditValues(submission);
                                          setIsEditing(false);
                                          setIsDrawerOpen(true);
                                        }}
                                        className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600 text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
                                      >
                                        <Eye className="h-4 w-4 mr-2" />
                                        View Details
                                      </button>
                                      <DeleteButton 
                                        style={`text-red-600 hover:text-red-800 hover:underline hover:underline-offset-4`} 
                                        onDelete={() => handleDelete(submission?.id, selectedForm?.id)}
                                        delete_icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-8">
                      <EmptyState message="No submissions for this form." />
                    </div>
                  )}

                </div>
              );
            })()
          )}
        </div>
        
        <Drawer 
          isOpen={isDrawerOpen} 
          onClose={() => setIsDrawerOpen(false)} 
          title="Submission Details" 
          border_radius='md:rounded-none' 
          showClose={false} 
          ChilDiv='' 
          ChildParent=''>
         {selectedSubmission ? (
          <div className="space-y-6 text-sm text-gray-800">
            {/* Form Header Card */}
            <div className="bg-slate-50 dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700 relative overflow-hidden shadow-lg">
              
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className='flex items-center'>
                    <div className="w-2 h-8 bg-blue-400 dark:bg-blue-600 rounded-full mr-4"></div>
                    <div className='flex items-center justify-between'>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedSubmission.formName}
                      </h3>
                    </div>
                  </div>
                  <button
                      onClick={() => setIsDrawerOpen(false)}
                      className="text-gray-500 dark:hover:text-gray-400 hover:text-gray-800 text-xl m-2"
                    >
                      ✖
                    </button>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                    Submitted on: {formatDate(selectedSubmission.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Enhanced Candidate Information Card */}
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 relative overflow-hidden backdrop-blur-sm">
              
              <div className="relative p-8">
                {/* Enhanced Section Header */}
                <div className="flex items-center mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <div className="w-12 h-12 bg-blue-600 dark:bg-blue-700 rounded-2xl flex items-center justify-center mr-4 shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="absolute -top-1 right-3 w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <h4 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                      Submitted Information
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Review and manage candidate details
                    </p>
                  </div>
                </div>

                {/* Enhanced Information Grid */}
                <div className="grid gap-5 overflow-auto max-h-[420px] p-6">
                  {Object.entries(selectedSubmission)
                    .filter(([key]) =>
                      !['created_at', 'updated_at', 'created_by', 'submission_id', 'id', 'form_id', 'formName'].includes(key)
                    )
                    .map(([key, value], index) => {

                    const colors = [
                      'from-blue-50 to-blue-100 dark:from-blue-800 dark:to-blue-700 border-slate-200 dark:border-slate-600',
                    ];
                    
                    const iconColors = [
                      'bg-blue-500',
                    ];
                    
                    const colorIndex = index % colors.length;

                    return (
                      <div
                        key={key}
                        className={`group relative bg-gradient-to-r ${colors[colorIndex]} rounded-2xl border-2 p-6 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1`}
                      >
                        {/* Decorative corner accent */}
                        <div className="absolute top-0 right-0 w-8 h-8 bg-white/20 dark:bg-black/10 rounded-bl-2xl rounded-tr-2xl"></div>
                        
                        <div className="flex items-start space-x-4">
                          {/* Enhanced Icon */}
                          <div className={`flex-shrink-0 w-10 h-10 ${iconColors[colorIndex]} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <label className="block font-semibold text-gray-800 dark:text-gray-200 text-sm mb-3 tracking-wide">
                              {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                            </label>
                            
                            <div className="space-y-2">
                              {typeof value === 'string' && value.startsWith('form_uploads') ? (
                                <a
                                  href={`https://hrmsnew.prepstripe.com/storage/${value}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-2 text-slate-700 dark:text-slate-300 font-semibold text-sm bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-xl transition-all duration-200 hover:bg-slate-200 dark:hover:bg-slate-700 hover:shadow-md transform hover:-translate-y-0.5"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  <span>Open Document</span>
                                </a>
                              ) : isEditing && key !== 'formName' && key !== 'created_at' ? (
                                <input
                                  type="text"
                                  value={editValues[key] || ''}
                                  onChange={(e) =>
                                    setEditValues((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 backdrop-blur-sm"
                                  placeholder="Enter value..."
                                />
                              ) : (
                                <div className="bg-white/50 dark:bg-gray-800/50 rounded-xl px-4 py-3 backdrop-blur-sm border border-white/20 dark:border-gray-700/20">
                                  <span className="font-semibold text-gray-900 dark:text-white text-sm block leading-relaxed">
                                    {typeof value === 'object' ? JSON.stringify(value) : value?.toString() || '-'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Enhanced Action Buttons */}
                <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => handleSubmitUpdate(selectedSubmission.id, selectedSubmission.form_id)}
                        className="group relative px-6 py-3 border border-blue-500 text-blue-600 font-semibold rounded-xl shadow-lg hover:bg-blue-50 hover:border-blue-600 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
                      >
                        <span className="relative z-10 flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Changes</span>
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditValues(selectedSubmission);
                        }}
                        className="group relative px-6 py-3 border border-slate-400 text-slate-700 dark:text-slate-200 font-semibold rounded-xl shadow-lg hover:bg-slate-50 hover:border-slate-500 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-700"
                      >
                        <span className="relative z-10 flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Cancel</span>
                        </span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="group relative px-6 py-3 border border-blue-500 text-blue-600 font-semibold rounded-xl shadow-lg hover:bg-blue-50 hover:border-blue-600 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
                    >
                      <span className="relative z-10 flex items-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>Edit Information</span>
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-96 p-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-xl font-medium mb-2">No data to display</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Select a submission to view details</p>
            </div>
          </div>
        )}
        </Drawer>

      </div>
    </div>
  );
};

export default FormResponsePage;